import type { PortfolioCollectionPlatform } from "@repo/shared/types";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { OptionRow } from "@/components/ui/option-row";
import { useLocalize } from "@/lib/i18n/use-localize";

import { platformLabel } from "./detail/portfolio-weights/platform-label";

type PlatformPickerProps = {
  platforms: PortfolioCollectionPlatform[];
  /** Selected platform row ids (empty = no platform). */
  value: string[];
  onChange: (platformIds: string[]) => void;
  label: string;
  hint: string;
  /** Distinguishes the checkbox ids when two pickers can be on screen. */
  idPrefix: string;
  disabled?: boolean;
};

/**
 * Platform multi-select of a portfolio (model or dynamic): one checkbox per
 * platform of the collection. Renders nothing when the collection has none.
 */
export function PlatformPicker({
  platforms,
  value,
  onChange,
  label,
  hint,
  idPrefix,
  disabled,
}: PlatformPickerProps) {
  const localize = useLocalize();

  if (platforms.length === 0) return null;

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div>
        {platforms.map((platform) => (
          <OptionRow key={platform.id} className="text-sm font-normal">
            <Checkbox
              id={`${idPrefix}-${platform.id}`}
              checked={value.includes(platform.id)}
              onCheckedChange={() =>
                onChange(
                  value.includes(platform.id)
                    ? value.filter((id) => id !== platform.id)
                    : [...value, platform.id],
                )
              }
              disabled={disabled}
            />
            <Label htmlFor={`${idPrefix}-${platform.id}`} className="font-normal cursor-pointer">
              {platformLabel(platforms, platform.id, localize)}
            </Label>
          </OptionRow>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}
